import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ClassificationRule } from '@balance/domain';
import { api, type AppUser, type ConceptDetail, type MailSource, type RawMailMessage, type SyncState } from '../api.js';
import { Modal } from '../components/Modal.js';
import { Badge, PendingHint } from '../components/ui.js';
import { centsToInput, eur, fmtDateTime, inputToCents } from '../format.js';
import { useApp } from '../state.js';

export function RulesPage() {
  const { catalog, run } = useApp();
  const [rules, setRules] = useState<ClassificationRule[] | null>(null);
  const [editing, setEditing] = useState<ClassificationRule | 'new' | null>(null);
  useEffect(() => {
    api.rules().then(setRules).catch(() => setRules([]));
  }, [catalog]);
  if (!catalog || !rules) return <div className="skeleton" />;
  const s = catalog.household.settings;
  const today = new Date().toISOString().slice(0, 10);
  const name = (id: string | null) => catalog.concepts.find((c) => c.id === id)?.name ?? '—';
  const fixed: Array<[string, string, 'activa' | 'configurable' | 'pendiente']> = [
    ['Calendario de ocio', `Viernes desde las ${s.weekendStartTime} (inclusive) hasta domingo 23:59; víspera de festivo desde las ${s.holidayEveStartTime} (inclusive). Sin hora conocida no se decide: se pregunta.`, 'activa'],
    ['Festivos', 'Viernes o lunes festivo se pega al fin de semana contiguo sin dotación extra. Martes a jueves: se pregunta el período. Un festivo repetido por varias fuentes cuenta una vez.', 'activa'],
    ['Fin de semana que cruza de mes', `El período pertenece al mes del ${s.weekendMonthRule === 'start' ? 'viernes' : 'domingo'} (confirmado por el hogar). Cada gasto admite asignación explícita distinta.`, 'activa'],
    ['Compra mensual de supermercado', `Más de ${eur(s.monthlyGroceryMinCents)} entre el día 25 y el 3 en «${name(s.monthlyGroceryConceptId)}» sugiere compra mensual del mes abastecido. Una principal por mes, siempre divisible; es control, no bloqueo.`, 'configurable'],
    ['Presupuestos consumibles', 'Mes abierto: proyección = máximo entre dotación y consumo. Mes cerrado: real; el remanente queda en el ahorro sin arrastre. Exceso en rojo con importe; la dotación no se amplía sola.', 'activa'],
    ['Servicios variables', 'La estimación por factura se reemplaza por el real, suba o baje. Nunca se suman estimación y real.', 'activa'],
    ['Sesiones y suscripciones', 'Se cuentan los vencimientos reales del calendario con sus excepciones y versiones de precio. Un pago puede cubrir varias ocurrencias o el mes completo.', 'activa'],
    ['Remitly y repartos', 'El envío se reparte íntegramente entre conceptos existentes o un gasto puntual nuevo; no se cuenta el padre además de sus partes. El residual sin repartir queda visible como pendiente.', 'activa'],
    ['Devoluciones de terceros', `Activación manual por parte. Vencen a las 00:00 del día ${s.receivableDueDays} desde el pago: el residual vuelve como costo propio al período original. Un cobro posterior al cierre es ingreso independiente.`, 'activa'],
    ['Transferencias propias', 'No generan ingreso ni gasto familiar.', 'activa'],
    ['Ingresos', 'Se distingue mes trabajado, cobro y mes financiado. Un abono vinculado a la nómina prevista no se suma dos veces.', 'activa'],
    ['Préstamos y cuotas', 'Entrada de financiación aparte del ahorro; la cuota afecta al disponible. No se infiere capital pendiente sin desglose de intereses. Klarna: un cobro nuevo se vincula al plan existente.', 'activa'],
  ];
  return (
    <div className="page">
      <div className="panel">
        <div className="panelhead">
          <div>
            <h2>Reglas de imputación por comercio</h2>
            <div className="muted">Sugieren destino según el texto del movimiento, con prioridad y vigencia. Nunca cambian una imputación ya decidida a mano.</div>
          </div>
          <button type="button" className="button primary" onClick={() => setEditing('new')}>
            + Crear regla
          </button>
        </div>
        {rules.length === 0 ? <p className="muted">Sin reglas. Las sugerencias usan sólo el calendario.</p> : null}
        {rules.map((r) => {
          const vigente = r.validFrom <= today && (r.validTo === null || r.validTo >= today);
          return (
            <div className="row" key={r.id}>
              <div>
                <strong>{r.name}</strong> <span className="muted">· contiene «{r.pattern.split('|').join('», «')}»</span>
                <div className="muted">
                  {r.mode === 'always' ? `Siempre ${name(r.conceptId)}` : r.mode === 'calendar' ? `Laborable: ${name(r.conceptId)} · ocio: ${name(r.leisureConceptId)}` : 'Sin destino automático: se pregunta'}
                  {r.askPeriod ? ' · pregunta período' : ''} · prioridad {r.priority} · desde {r.validFrom}
                  {r.validTo ? ` hasta ${r.validTo}` : ''}
                  {r.note ? ` · ${r.note}` : ''}
                </div>
              </div>
              <div className="inline">
                <Badge kind={vigente ? '' : 'neutral'}>{vigente ? 'vigente' : 'no vigente'}</Badge>
                <button type="button" className="button small" onClick={() => setEditing(r)}>
                  Editar
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="panel">
        <div className="panelhead">
          <div>
            <h2>Reglas de cálculo y calendario</h2>
            <div className="muted">Lo que el motor aplica hoy, lo configurable en Configuración y lo que sigue pendiente de decisión del hogar.</div>
          </div>
          <Link className="button" to="/configuracion">
            Ajustar parámetros
          </Link>
        </div>
        {fixed.map(([n, text, status]) => (
          <div className="row" key={n}>
            <div>
              <strong>{n}</strong>
              <div className="muted">{text}</div>
            </div>
            <Badge kind={status === 'activa' ? '' : status === 'configurable' ? 'neutral' : 'coral'}>{status}</Badge>
          </div>
        ))}
      </div>
      <PendingHint>Una clasificación guardada en un movimiento no crea una regla permanente. Las reglas se aplicarán también a los avisos importados cuando exista la importación de correo (etapa 5).</PendingHint>
      {editing ? <RuleModal rule={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); api.rules().then(setRules).catch(() => undefined); }} /> : null}
    </div>
  );
}

function RuleModal({ rule, onClose, onSaved }: { rule: ClassificationRule | null; onClose: () => void; onSaved: () => void }) {
  const { catalog, run } = useApp();
  const [form, setForm] = useState({
    name: rule?.name ?? '',
    pattern: rule?.pattern ?? '',
    mode: rule?.mode ?? 'always',
    conceptId: rule?.conceptId ?? '',
    leisureConceptId: rule?.leisureConceptId ?? '',
    askPeriod: rule?.askPeriod ?? false,
    priority: String(rule?.priority ?? 100),
    validFrom: rule?.validFrom ?? new Date().toISOString().slice(0, 10),
    validTo: rule?.validTo ?? '',
    note: rule?.note ?? '',
  });
  const [error, setError] = useState<string | null>(null);
  const expenses = catalog?.concepts.filter((c) => c.direction === 'expense') ?? [];
  async function save() {
    if (!form.name.trim() || !form.pattern.trim()) return setError('Nombre y texto a buscar son obligatorios.');
    if (form.mode !== 'ask' && !form.conceptId) return setError('Elegí el destino.');
    if (form.mode === 'calendar' && !form.leisureConceptId) return setError('Elegí el destino en ocio.');
    const payload = { name: form.name.trim(), pattern: form.pattern.trim(), mode: form.mode, conceptId: form.mode === 'ask' ? null : form.conceptId, leisureConceptId: form.mode === 'calendar' ? form.leisureConceptId : null, askPeriod: form.askPeriod, priority: Number(form.priority) || 0, validFrom: form.validFrom, validTo: form.validTo || null, note: form.note || null };
    const ok = await run(() => (rule ? api.updateRule(rule.id, payload) : api.createRule(payload)), rule ? 'Regla actualizada (las imputaciones existentes no cambian)' : 'Regla creada');
    if (ok) onSaved();
  }
  return (
    <Modal
      title={rule ? `Regla · ${rule.name}` : 'Crear regla'}
      onClose={onClose}
      error={error}
      actions={
        <>
          {rule ? (
            <button type="button" className="button danger left" onClick={() => void run(() => api.deleteRule(rule.id), 'Regla eliminada').then((r) => r !== undefined && onSaved())}>
              Eliminar
            </button>
          ) : null}
          <button type="button" className="button primary" onClick={() => void save()}>
            Guardar regla
          </button>
        </>
      }
    >
      <div className="field-row">
        <label>
          Nombre
          <input data-autofocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label>
          Cuando la descripción contenga (varios con |)
          <input value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })} placeholder="vivari|macxipa" />
        </label>
      </div>
      <label>
        Calendario
        <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value as ClassificationRule['mode'] })}>
          <option value="always">Siempre este destino (también en fin de semana)</option>
          <option value="calendar">Aplicar calendario de ocio (laborable / fin de semana)</option>
          <option value="ask">Sin destino automático: preguntar (no imputar a ocio)</option>
        </select>
      </label>
      {form.mode !== 'ask' ? (
        <div className="field-row">
          <label>
            {form.mode === 'calendar' ? 'Destino en laborable' : 'Destino'}
            <select value={form.conceptId} onChange={(e) => setForm({ ...form, conceptId: e.target.value })}>
              <option value="">Elegir…</option>
              {expenses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          {form.mode === 'calendar' ? (
            <label>
              Destino en ocio
              <select value={form.leisureConceptId} onChange={(e) => setForm({ ...form, leisureConceptId: e.target.value })}>
                <option value="">Elegir…</option>
                {expenses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      ) : null}
      <label className="check">
        <input type="checkbox" checked={form.askPeriod} onChange={(e) => setForm({ ...form, askPeriod: e.target.checked })} />
        Preguntar siempre el período (no aplicar automáticamente)
      </label>
      <div className="field-row three">
        <label>
          Prioridad (mayor gana)
          <input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
        </label>
        <label>
          Vigente desde
          <input type="date" value={form.validFrom} onChange={(e) => setForm({ ...form, validFrom: e.target.value })} />
        </label>
        <label>
          Hasta (vacío = indefinido)
          <input type="date" value={form.validTo} onChange={(e) => setForm({ ...form, validTo: e.target.value })} />
        </label>
      </div>
      <label>
        Explicación
        <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
      </label>
      <p className="muted">Sólo futuras sugerencias: las imputaciones ya decididas a mano no se tocan.</p>
    </Modal>
  );
}

export function SyncPage() {
  const { balance, movements, run, toast, refresh } = useApp();
  const [state, setState] = useState<SyncState | null>(null);
  const [newSource, setNewSource] = useState<{ label: string; kind: 'gmail' | 'file'; address: string; query: string; envPrefix: string } | null>(null);
  const [period, setPeriod] = useState({ from: '', to: '' });
  const [busy, setBusy] = useState(false);
  const load = () => api.sync().then(setState).catch((e) => toast(e instanceof Error ? e.message : 'Error', 'error'));
  useEffect(() => {
    void load();
  }, [balance]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!state) return <div className="skeleton" />;
  const txById = new Map((movements?.transactions ?? []).map((t) => [t.id, t]));
  const fileSource = state.sources.find((s) => s.kind === 'file');

  async function importFile(file: File) {
    setBusy(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text) as { messages?: unknown[] } | unknown[];
      const messages = (Array.isArray(data) ? data : data.messages ?? []) as RawMailMessage[];
      if (!messages.length) throw new Error('El archivo no contiene mensajes (se espera { messages: [...] }).');
      let source = fileSource;
      if (!source) source = { ...(await api.createSource({ label: 'Archivo de avisos', kind: 'file' })), gmailConfigured: false };
      const r = await api.importMessages(source.id, messages, `archivo ${file.name}`);
      toast(`Lote ${r.run.status}: ${r.run.imported} nuevos · ${r.run.duplicates} ya registrados · ${r.run.candidates} candidatos · ${r.run.unparsed} sin datos`);
      await refresh();
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo importar', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function runGmail(source: MailSource, window?: { from: string; to: string }) {
    setBusy(true);
    try {
      const r = await api.runSync(source.id, window ? { from: new Date(`${window.from}T00:00:00`).toISOString(), to: new Date(`${window.to}T23:59:59`).toISOString() } : {});
      toast(`Gmail ${r.run.status}: ${r.run.fetched} avisos · ${r.run.imported} nuevos · ${r.run.candidates} candidatos`);
      await refresh();
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo sincronizar', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="grid">
        <div>
          <div className="panel">
            <div className="panelhead">
              <h2>Fuentes de avisos</h2>
              <button type="button" className="button" onClick={() => setNewSource({ label: 'Gmail avisos', kind: 'gmail', address: '', query: 'label:santander', envPrefix: 'GMAIL' })}>
                + Fuente Gmail
              </button>
            </div>
            {state.sources.length === 0 ? <p className="muted">Sin fuentes. Importar un archivo crea una fuente «Archivo de avisos».</p> : null}
            {state.sources.map((s) => (
              <div className="row" key={s.id}>
                <div>
                  <strong>{s.label}</strong> <Badge kind={s.kind === 'gmail' ? (s.gmailConfigured ? '' : 'warn') : 'neutral'}>{s.kind === 'gmail' ? (s.gmailConfigured ? 'Gmail · credenciales presentes' : 'Gmail · sin credenciales en el entorno') : 'Archivo'}</Badge>
                  <div className="muted">
                    {s.address ? `${s.address} · ` : ''}
                    {s.config.query ? `consulta «${s.config.query}» · ` : ''}
                    último éxito {fmtDateTime(s.lastSuccessAt)} · cursor {s.checkpoint.lastReceivedAt ? fmtDateTime(s.checkpoint.lastReceivedAt) : 'sin iniciar'}
                  </div>
                </div>
                <div className="inline">
                  {s.kind === 'gmail' ? (
                    <button type="button" className="button small primary" disabled={!s.gmailConfigured || busy} title={s.gmailConfigured ? 'Recuperar desde el último éxito con margen' : `Definir ${s.config.envPrefix ?? 'GMAIL'}_CLIENT_ID/_CLIENT_SECRET/_REFRESH_TOKEN`} onClick={() => void runGmail(s)}>
                      Sincronizar ahora
                    </button>
                  ) : null}
                  <button type="button" className="button small" onClick={() => void run(() => api.deleteSource(s.id), 'Fuente eliminada').then(load)}>
                    Quitar
                  </button>
                </div>
              </div>
            ))}
            <div className="split" style={{ marginTop: 10 }}>
              <h3>Importar un archivo de avisos</h3>
              <p className="muted">JSON con <code>{'{ messages: [{ messageId, receivedAt, subject, from, body }] }'}</code>. Cada mensaje se importa una sola vez por fuente; los que coinciden con un movimiento manual o con otro aviso quedan como candidatos, nunca se fusionan solos.</p>
              <input type="file" accept="application/json,.json" aria-label="Archivo de avisos" disabled={busy} onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) void importFile(file);
                }}
              />
            </div>
            {state.sources.some((s) => s.kind === 'gmail' && s.gmailConfigured) ? (
              <div className="split">
                <h3>Revisar un período</h3>
                <div className="field-row">
                  <label>
                    Desde
                    <input type="date" value={period.from} onChange={(e) => setPeriod({ ...period, from: e.target.value })} />
                  </label>
                  <label>
                    Hasta
                    <input type="date" value={period.to} onChange={(e) => setPeriod({ ...period, to: e.target.value })} />
                  </label>
                </div>
                <button type="button" className="button" disabled={!period.from || !period.to || busy} onClick={() => void runGmail(state.sources.find((s) => s.kind === 'gmail' && s.gmailConfigured)!, period)}>
                  Volver a buscar sin duplicar
                </button>
              </div>
            ) : null}
          </div>

          <div className="panel">
            <h2>Candidatos de conciliación</h2>
            <p className="muted">Avisos que podrían ser el mismo gasto que un movimiento ya cargado o que otro aviso reenviado. Decidir no fusiona por importe y día a ciegas.</p>
            {state.candidates.length === 0 ? <p className="muted">Ninguno pendiente.</p> : null}
            {state.candidates.map((m) => {
              const t = m.candidateTransactionId ? txById.get(m.candidateTransactionId) : null;
              return (
                <div className="row" key={m.id}>
                  <div>
                    <strong>{m.parsed?.merchant ?? m.subject}</strong> · {eur(m.parsed?.amountCents ?? null)} · {m.parsed?.date}
                    {m.parsed?.time ? ` ${m.parsed.time}` : ''}
                    <div className="muted">
                      Aviso {m.messageId} de {m.sender} · recibido {fmtDateTime(m.receivedAt)}
                      {t ? ` · coincide con «${t.description}» del ${t.date} (${t.source})` : m.candidateMessageId ? ' · coincide con otro aviso ya importado (reenvío)' : ''}
                    </div>
                  </div>
                  <div className="inline">
                    {m.candidateTransactionId ? (
                      <button type="button" className="button small primary" onClick={() => void run(() => api.resolveMessage(m.id, 'link'), 'Aviso vinculado al movimiento existente').then(load)}>
                        Es el mismo
                      </button>
                    ) : null}
                    <button type="button" className="button small" onClick={() => void run(() => api.resolveMessage(m.id, 'create'), 'Movimiento creado desde el aviso').then(load)}>
                      Es distinto: crear
                    </button>
                    <button type="button" className="button small" onClick={() => void run(() => api.resolveMessage(m.id, 'dismiss'), 'Aviso descartado').then(load)}>
                      Descartar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {state.unparsed.length ? (
            <div className="panel">
              <h2>Avisos sin datos suficientes</h2>
              <p className="muted">No se adivina importe, signo, comercio ni fecha. Se pueden cargar a mano desde Movimientos.</p>
              {state.unparsed.map((m) => (
                <div className="row" key={m.id}>
                  <div>
                    <strong>{m.subject}</strong>
                    <div className="muted">
                      {m.sender} · {fmtDateTime(m.receivedAt)} · faltan: {m.parsed?.missing.join(', ')}
                    </div>
                  </div>
                  <button type="button" className="button small" onClick={() => void run(() => api.resolveMessage(m.id, 'dismiss'), 'Aviso descartado').then(load)}>
                    Descartar
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <div>
          <div className="panel">
            <div className="panelhead">
              <h2>Correo de alertas</h2>
              <Badge kind={state.sources.some((s) => s.gmailConfigured) ? '' : 'warn'}>{state.sources.some((s) => s.gmailConfigured) ? 'Gmail configurado' : 'Gmail sin configurar'}</Badge>
            </div>
            <div className="receipt">Santander → Gmail (OAuth sólo lectura) → avisos → candidatos → movimientos → reglas → balance</div>
            <p className="muted" style={{ marginTop: 12 }}>
              El cliente Gmail usa la API oficial con credenciales de la propia aplicación en variables de entorno; no se ha validado con un buzón real ni se afirma cobertura de los avisos de Santander hasta revisar muestras autorizadas. El cursor avanza sólo al persistir cada lote; la recuperación parte del último éxito con tres días de margen y deduplica.
            </p>
          </div>
          <div className="panel">
            <h2>Registro de lotes</h2>
            {state.runs.length === 0 ? <p className="muted">Sin ejecuciones todavía.</p> : null}
            {state.runs.map((r) => (
              <div className="row" key={r.id}>
                <div>
                  <Badge kind={r.status === 'success' ? '' : r.status === 'failed' ? 'red' : 'warn'}>{r.status}</Badge> {fmtDateTime(r.startedAt)}
                  <div className="muted">
                    {r.fetched} recibidos · {r.imported} nuevos · {r.duplicates} ya registrados · {r.candidates} candidatos · {r.unparsed} sin datos{r.note ? ` · ${r.note}` : ''}
                    {r.errors.length ? ` · ${r.errors.length} errores recuperables` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {newSource ? (
        <Modal
          title="Nueva fuente Gmail"
          onClose={() => setNewSource(null)}
          actions={
            <button type="button" className="button primary" onClick={() => void run(() => api.createSource({ label: newSource.label, kind: 'gmail', address: newSource.address || null, config: { query: newSource.query, envPrefix: newSource.envPrefix } }), 'Fuente creada; faltan las credenciales en el entorno').then(() => { setNewSource(null); void load(); })}>
              Crear fuente
            </button>
          }
        >
          <p className="muted">Las credenciales OAuth nunca se guardan en la base ni en Git: se leen de <code>{newSource.envPrefix}_CLIENT_ID</code>, <code>{newSource.envPrefix}_CLIENT_SECRET</code> y <code>{newSource.envPrefix}_REFRESH_TOKEN</code> con alcance de sólo lectura.</p>
          <div className="field-row">
            <label>
              Etiqueta
              <input data-autofocus value={newSource.label} onChange={(e) => setNewSource({ ...newSource, label: e.target.value })} />
            </label>
            <label>
              Buzón (informativo)
              <input value={newSource.address} onChange={(e) => setNewSource({ ...newSource, address: e.target.value })} placeholder="nombre@gmail.com" />
            </label>
            <label>
              Consulta Gmail
              <input value={newSource.query} onChange={(e) => setNewSource({ ...newSource, query: e.target.value })} placeholder="label:santander" />
            </label>
            <label>
              Prefijo de variables de entorno
              <input value={newSource.envPrefix} onChange={(e) => setNewSource({ ...newSource, envPrefix: e.target.value.toUpperCase() })} />
            </label>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

export function SettingsPage() {
  const { catalog, balance, reduced, setReduced, run, refresh } = useApp();
  const [form, setForm] = useState<Record<string, string>>({});
  const [holiday, setHoliday] = useState({ date: '', name: '', scope: 'nacional' });
  const [audit, setAudit] = useState<ConceptDetail['audit']>([]);
  useEffect(() => {
    if (!catalog) return;
    const s = catalog.household.settings;
    setForm({
      name: catalog.household.name,
      openingPeriod: catalog.household.openingPeriod,
      openingBalance: centsToInput(catalog.household.openingBalanceCents),
      horizonMonths: String(s.horizonMonths),
      horizonIncludesCurrent: s.horizonIncludesCurrent ? '1' : '0',
      weekendMonthRule: s.weekendMonthRule,
      weekendStartTime: s.weekendStartTime,
      holidayEveStartTime: s.holidayEveStartTime,
      receivableDueDays: String(s.receivableDueDays),
      monthlyGroceryMin: centsToInput(s.monthlyGroceryMinCents),
      monthlyGroceryConceptId: s.monthlyGroceryConceptId ?? '',
    });
    api.audit({ limit: 30 }).then(setAudit).catch(() => undefined);
  }, [catalog]);
  if (!catalog || !balance) return <div className="skeleton" />;
  const f = (k: string) => form[k] ?? '';
  const set = (k: string, v: string) => setForm((x) => ({ ...x, [k]: v }));
  async function save() {
    const opening = inputToCents(f('openingBalance'));
    const grocery = inputToCents(f('monthlyGroceryMin'));
    await run(
      () =>
        api.updateHousehold({
          name: f('name'),
          openingPeriod: f('openingPeriod'),
          openingBalanceCents: opening ?? 0,
          settings: {
            horizonMonths: Number(f('horizonMonths')),
            horizonIncludesCurrent: f('horizonIncludesCurrent') === '1',
            weekendMonthRule: f('weekendMonthRule') as 'start' | 'end',
            weekendStartTime: f('weekendStartTime'),
            holidayEveStartTime: f('holidayEveStartTime'),
            receivableDueDays: Number(f('receivableDueDays')),
            monthlyGroceryMinCents: grocery ?? 0,
            monthlyGroceryConceptId: f('monthlyGroceryConceptId') || null,
          },
        }),
      'Configuración guardada y balance recalculado',
    );
  }
  return (
    <div className="page">
      <div className="grid">
        <div>
          <div className="panel">
            <h2>Hogar y punto de partida</h2>
            <div className="field-row">
              <label>
                Nombre del hogar
                <input value={f('name')} onChange={(e) => set('name', e.target.value)} />
              </label>
              <label>
                Personas
                <input value={catalog.persons.map((p) => p.name).join(', ')} readOnly />
              </label>
              <label>
                Primer mes con acumulado conocido (YYYY-MM)
                <input value={f('openingPeriod')} onChange={(e) => set('openingPeriod', e.target.value)} />
              </label>
              <label>
                Acumulado al inicio de ese mes (€)
                <input inputMode="decimal" value={f('openingBalance')} onChange={(e) => set('openingBalance', e.target.value)} />
              </label>
            </div>
            <PendingHint>El origen y la fecha del saldo inicial real deben validarse antes de importar un dato real (DECISIONES.md #6). El valor cargado es de ejemplo.</PendingHint>
          </div>
          <div className="panel">
            <h2>Horizonte y calendario</h2>
            <div className="field-row">
              <label>
                Meses de proyección
                <input type="number" min={1} max={36} value={f('horizonMonths')} onChange={(e) => set('horizonMonths', e.target.value)} />
              </label>
              <label>
                Las columnas del horizonte
                <select value={f('horizonIncludesCurrent')} onChange={(e) => set('horizonIncludesCurrent', e.target.value)}>
                  <option value="1">Incluyen el mes actual (actual + 11, confirmado)</option>
                  <option value="0">Empiezan en el mes siguiente</option>
                </select>
              </label>
              <label>
                Fin de semana que cruza de mes pertenece a
                <select value={f('weekendMonthRule')} onChange={(e) => set('weekendMonthRule', e.target.value)}>
                  <option value="start">Mes del viernes (regla del hogar)</option>
                  <option value="end">Mes del domingo</option>
                </select>
              </label>
              <label>
                Días hasta vencer un recuperable
                <input type="number" min={0} value={f('receivableDueDays')} onChange={(e) => set('receivableDueDays', e.target.value)} />
              </label>
              <label>
                Viernes es ocio desde
                <input type="time" value={f('weekendStartTime')} onChange={(e) => set('weekendStartTime', e.target.value)} />
              </label>
              <label>
                Víspera de festivo es ocio desde
                <input type="time" value={f('holidayEveStartTime')} onChange={(e) => set('holidayEveStartTime', e.target.value)} />
              </label>
              <label>
                Umbral de compra mensual de supermercado (€)
                <input inputMode="decimal" value={f('monthlyGroceryMin')} onChange={(e) => set('monthlyGroceryMin', e.target.value)} />
              </label>
              <label>
                Concepto de supermercado para esa sugerencia
                <select value={f('monthlyGroceryConceptId')} onChange={(e) => set('monthlyGroceryConceptId', e.target.value)}>
                  <option value="">Ninguno</option>
                  {catalog.concepts.filter((c) => c.behavior === 'budget').map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="hint">Horizonte «actual + 11», fin de semana en el mes de su viernes, límites 14:00 y 16:00 inclusivos y vencimiento a los 10 días son reglas confirmadas del hogar; se dejan editables por si cambian.</div>
            <button type="button" className="button primary" style={{ marginTop: 12 }} onClick={() => void save()}>
              Guardar configuración
            </button>
          </div>
          <div className="panel">
            <h2>Festivos aplicables</h2>
            <p className="muted">Nacionales, de Catalunya y locales de Barcelona cuando correspondan oficialmente, sin duplicarlos por fuente. {catalog.holidays.some((h) => h.source === 'seed-ejemplo') ? 'Los cargados son de ejemplo: no son el calendario oficial validado.' : ''}</p>
            {catalog.holidays.map((h) => (
              <div className="row" key={h.id}>
                <div>
                  {h.date} · {h.name} <Badge kind="neutral">{h.scope}</Badge>
                  {h.source ? <span className="muted"> · {h.source}</span> : null}
                </div>
                <button type="button" className="button small" onClick={() => void run(() => api.deleteHoliday(h.id), 'Festivo eliminado')}>
                  Quitar
                </button>
              </div>
            ))}
            <div className="field-row three" style={{ marginTop: 12 }}>
              <label>
                Fecha
                <input type="date" value={holiday.date} onChange={(e) => setHoliday({ ...holiday, date: e.target.value })} />
              </label>
              <label>
                Nombre
                <input value={holiday.name} onChange={(e) => setHoliday({ ...holiday, name: e.target.value })} />
              </label>
              <label>
                Ámbito
                <select value={holiday.scope} onChange={(e) => setHoliday({ ...holiday, scope: e.target.value })}>
                  <option value="nacional">Nacional</option>
                  <option value="autonomico">Catalunya</option>
                  <option value="local">Barcelona</option>
                </select>
              </label>
            </div>
            <button type="button" className="button" disabled={!holiday.date || !holiday.name} onClick={() => void run(() => api.addHoliday({ ...holiday, source: 'manual' }), 'Festivo añadido').then(() => setHoliday({ date: '', name: '', scope: 'nacional' }))}>
              + Añadir festivo
            </button>
          </div>
        </div>
        <div>
          <UsersPanel />
          <div className="panel">
            <h2>Visualización</h2>
            <label className="check">
              <input type="checkbox" checked={reduced} onChange={(e) => setReduced(e.target.checked)} />
              Reducir animaciones (también se respeta la preferencia del sistema)
            </label>
            <div className="row">
              <div>Última vista</div>
              <span className="muted">Se recuerda al volver a abrir</span>
            </div>
            <div className="row">
              <div>Novedades vistas</div>
              <span className="muted">Cursor por usuario, separado del de sincronización</span>
            </div>
            <div className="row">
              <div>Zona horaria</div>
              <span className="muted">{catalog.household.timeZone}</span>
            </div>
          </div>
          <div className="panel">
            <h2>Mantenimiento</h2>
            <p className="muted">Jobs idempotentes: se ejecutan al arrancar y se pueden forzar aquí.</p>
            <button type="button" className="button" onClick={() => void run(() => api.runCloseOverdue(), 'Recuperables vencidos revisados')}>
              Cerrar recuperables vencidos
            </button>
            <button type="button" className="button" style={{ marginLeft: 8 }} onClick={() => void refresh()}>
              Recargar datos
            </button>
          </div>
          <div className="panel">
            <h2>Historial reciente</h2>
            {audit.length === 0 ? <p className="muted">Sin cambios registrados.</p> : null}
            {audit.map((a) => (
              <div className="row" key={a.id}>
                <div>
                  <Badge kind="neutral">{a.entity}</Badge> {a.action}
                  <div className="muted">{fmtDateTime(a.at)} · {a.actor}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Usuarios del hogar: cada persona con su contraseña, sus sesiones y sus novedades vistas. */
function UsersPanel() {
  const { auth, catalog, run, refresh } = useApp();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [form, setForm] = useState({ username: '', displayName: '', password: '', personId: '' });
  const [pwd, setPwd] = useState({ current: '', next: '' });
  const [error, setError] = useState<string | null>(null);
  const load = () => api.users().then(setUsers).catch(() => setUsers([]));
  useEffect(() => {
    void load();
  }, []);
  async function create() {
    setError(null);
    if (form.password.length < 8) return setError('La contraseña debe tener al menos 8 caracteres.');
    const first = users.length === 0;
    const ok = await run(() => api.createUser({ username: form.username.trim().toLowerCase(), displayName: form.displayName.trim(), password: form.password, personId: form.personId || null }), first ? 'Primer usuario creado: desde ahora cada persona entra con su usuario' : 'Usuario creado');
    if (ok) {
      setForm({ username: '', displayName: '', password: '', personId: '' });
      if (first) await refresh();
      else void load();
    }
  }
  return (
    <div className="panel">
      <h2>Usuarios</h2>
      <p className="muted">
        {auth?.mode === 'users'
          ? 'Cada persona entra con su usuario; las novedades vistas y el historial de cambios son de cada uno.'
          : 'Todavía no hay usuarios. Al crear el primero, la app pedirá usuario y contraseña y la contraseña común deja de valer.'}
      </p>
      {users.map((u) => (
        <div className="row" key={u.id}>
          <div>
            <strong>{u.displayName}</strong> <span className="muted">· {u.username}</span>
            {auth?.user?.username === u.username ? <Badge>conectado</Badge> : null}
          </div>
          {auth?.user?.username !== u.username && users.length > 1 ? (
            <button type="button" className="button small" onClick={() => void run(() => api.deleteUser(u.username), 'Usuario eliminado').then(load)}>
              Quitar
            </button>
          ) : null}
        </div>
      ))}
      <div className="field-row" style={{ marginTop: 12 }}>
        <label>
          Usuario
          <input autoCapitalize="none" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="mariana" />
        </label>
        <label>
          Nombre visible
          <input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} placeholder="Mariana" />
        </label>
        <label>
          Contraseña inicial
          <input type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </label>
        <label>
          Persona del hogar
          <select value={form.personId} onChange={(e) => setForm({ ...form, personId: e.target.value })}>
            <option value="">—</option>
            {catalog?.persons.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error ? <div className="error">{error}</div> : null}
      <button type="button" className="button" disabled={!form.username || !form.password} onClick={() => void create()}>
        + Crear usuario
      </button>
      {auth?.mode === 'users' && auth.user ? (
        <div className="split" style={{ marginTop: 16 }}>
          <h3>Cambiar mi contraseña</h3>
          <div className="field-row">
            <label>
              Actual
              <input type="password" autoComplete="current-password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} />
            </label>
            <label>
              Nueva
              <input type="password" autoComplete="new-password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} />
            </label>
          </div>
          <button type="button" className="button" disabled={!pwd.current || !pwd.next} onClick={() => void run(() => api.changeMyPassword(pwd.current, pwd.next), 'Contraseña actualizada').then((ok) => ok && setPwd({ current: '', next: '' }))}>
            Guardar contraseña
          </button>
        </div>
      ) : null}
    </div>
  );
}
